//
// Copyright © 2026 PlayJoy Studios.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

import type { AttachedDoc, Class, Doc, Markup, Mixin, Permission, Ref, Role, Timestamp } from '@hcengineering/core'
import type { Asset, Metadata, Plugin, Resource } from '@hcengineering/platform'
import { IntlString, plugin } from '@hcengineering/platform'
import type { Project } from '@hcengineering/tracker'
import type { AnyComponent } from '@hcengineering/ui'

/**
 * @public
 */
export const aiConsoleId = 'ai-console' as Plugin

/**
 * @public
 */
export type MessageRole = 'user' | 'assistant' | 'system'

/**
 * @public
 */
export type AiLevel = 'low' | 'medium' | 'high'

/**
 * @public
 */
export interface AiUsage {
  input: number
  cachedInput: number
  output: number
  reasoning: number
}

/**
 * @public
 */
export interface Conversation extends Doc {
  provider: string
  model: string
  effort: string
  project?: Ref<Project>
  contextTokens: number
  windowTokens: number
  startedOn: Timestamp
  title?: string
}

/**
 * @public
 */
export interface Message extends AttachedDoc {
  role: MessageRole
  text: Markup
  usage?: AiUsage
  error?: string
  level?: AiLevel
  model?: string
}

/**
 * @public
 */
export interface AiProject extends Project {
  repoUrl: string
  repoRef: string
  repoEnabled: boolean
}

export * from './access'
export * from './levels'
export * from './rest'
export * from './version'

export default plugin(aiConsoleId, {
  class: {
    Conversation: '' as Ref<Class<Conversation>>,
    Message: '' as Ref<Class<Message>>
  },
  mixin: {
    AiProject: '' as Ref<Mixin<AiProject>>
  },
  component: {
    Console: '' as AnyComponent,
    ConsoleWidget: '' as AnyComponent,
    WidgetSwitcher: '' as AnyComponent,
    ProjectSettings: '' as AnyComponent
  },
  function: {
    CanUseConsole: '' as Resource<() => Promise<boolean>>
  },
  icon: {
    AiConsole: '' as Asset
  },
  string: {
    ApplicationLabel: '' as IntlString,
    Conversation: '' as IntlString,
    Message: '' as IntlString,
    Ask: '' as IntlString,
    Placeholder: '' as IntlString,
    Clear: '' as IntlString,
    ClearConfirm: '' as IntlString,
    Context: '' as IntlString,
    ContextWarning: '' as IntlString,
    Level: '' as IntlString,
    LevelLow: '' as IntlString,
    LevelMedium: '' as IntlString,
    LevelHigh: '' as IntlString,
    Project: '' as IntlString,
    NoProject: '' as IntlString,
    Thinking: '' as IntlString,
    Empty: '' as IntlString,
    ServiceUnavailable: '' as IntlString,
    RateLimited: '' as IntlString,
    HistoryTrimmed: '' as IntlString,
    ProjectSettings: '' as IntlString,
    ProjectRepo: '' as IntlString,
    RepoUrl: '' as IntlString,
    RepoUrlDescription: '' as IntlString,
    RepoUrlPlaceholder: '' as IntlString,
    RepoRef: '' as IntlString,
    RepoRefPlaceholder: '' as IntlString,
    RepoEnabled: '' as IntlString,
    ContextLabel: '' as IntlString,
    ContextOff: '' as IntlString,
    ProjectFromObject: '' as IntlString,
    ModuleVersion: '' as IntlString,
    NoAccess: '' as IntlString,
    UsePermission: '' as IntlString,
    UsePermissionDescription: '' as IntlString
  },
  permission: {
    UseConsole: '' as Ref<Permission>
  },
  role: {
    Assistant: '' as Ref<Role>
  },
  metadata: {
    EndpointURL: '' as Metadata<string>
  }
})
